<script setup lang="ts">
import { isVNode, Text } from "vue";
import { TooltipArrow, TooltipContent, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger } from "reka-ui";

import { useWindow } from "@/hooks/use-window";

import type { TooltipContentProps, TooltipRootProps } from "reka-ui";
import type { VNode, VNodeChild, VNodeNormalizedChildren } from "vue";

defineProps<Partial<{ contentProps: TooltipContentProps; rootProps: TooltipRootProps }>>();
const slots = defineSlots<{ trigger(): VNode[]; content(): VNode[] }>();

if (import.meta.env.DEV) {
  const triggerChildren = slots.trigger();

  function hasValidContent(node: VNode | VNodeNormalizedChildren | VNodeChild): boolean {
    if (isVNode(node)) {
      // HTML Element found. W
      if (typeof node.type === "string") return true;

      // Custom component found
      if (typeof node.type === "object") return true;

      // Text component, which we don't want
      if (node.type === Text) return false;

      // Typically a Fragment, loop through its children
      if (Array.isArray(node.children)) return node.children.length > 0 && node.children.every(hasValidContent);

      return hasValidContent(node.children);
    }

    if (Array.isArray(node)) return node.length > 0 && node.every(hasValidContent);

    return false;
  }

  // Ensure the content provided is valid
  if (!hasValidContent(triggerChildren)) {
    console.error("The tooltip trigger requires a wrapper HTML element");
    console.log(slots.content());
    throw new Error("A HTML element is required");
  }
}

const window = useWindow();
</script>
<template>
  <TooltipProvider :delay-duration="rootProps?.delayDuration ?? 100">
    <TooltipRoot :="rootProps">
      <TooltipTrigger :as-child="true">
        <slot name="trigger" />
      </TooltipTrigger>
      <TooltipPortal :to="window.document.body">
        <TooltipContent
          :="contentProps"
          hide-when-detached
          :avoid-collisions="true"
          :collision-padding="contentProps?.collisionPadding ?? 10"
          class="tw-z-[1000] tw-select-none tw-rounded-md tw-border tw-border-black/25 tw-bg-white tw-px-4 tw-py-2 tw-text-xs tw-shadow"
        >
          <slot name="content" />
          <TooltipArrow class="tw-fill-white tw-stroke-black/25 tw-stroke-1" size="8" />
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  </TooltipProvider>
</template>
